import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { getPinnedAwareVisibleLeafColumns } from '../table/tableHeadUseCase';
import {
  buildTableFindIndex,
  cycleFindMatchIndex,
  findMatchingRows,
} from './tableFindUseCase';

/**
 * @typedef {import('./tableFindUseCase').TableFindEntry} TableFindEntry
 */

/**
 * @typedef {object} TableFindScrollTarget
 * @property {string} groupId
 * @property {string} rowId
 * @property {number} rowIndexInGroup
 */

/**
 * @typedef {object} TableFindScrollContextValue
 * @property {TableFindScrollTarget | null} scrollTarget
 * @property {string | null} highlightedRowId
 * @property {() => void} onScrollTargetConsumed
 */

/** @type {React.Context<TableFindScrollContextValue | null>} */
const TableFindScrollContext = createContext(null);

const emptyFindScroll = Object.freeze({
  scrollTarget: null,
  highlightedRowId: null,
  onScrollTargetConsumed: () => {},
});

/**
 * @param {object} props
 * @param {TableFindScrollTarget | null} props.scrollTarget
 * @param {string | null} props.highlightedRowId
 * @param {() => void} props.onScrollTargetConsumed
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
export function TableFindProvider({
  scrollTarget,
  highlightedRowId,
  onScrollTargetConsumed,
  children,
}) {
  const value = useMemo(
    () => ({
      scrollTarget,
      highlightedRowId,
      onScrollTargetConsumed,
    }),
    [scrollTarget, highlightedRowId, onScrollTargetConsumed],
  );

  return (
    <TableFindScrollContext.Provider value={value}>
      {children}
    </TableFindScrollContext.Provider>
  );
}

/**
 * @returns {TableFindScrollContextValue}
 */
export function useTableFindScrollOptional() {
  return useContext(TableFindScrollContext) ?? emptyFindScroll;
}

/**
 * @param {EventTarget | null} target
 * @returns {boolean}
 */
function isEventInsideDialog(target) {
  return target instanceof Element && Boolean(target.closest('dialog, [role="dialog"]'));
}

/**
 * @param {EventTarget | null} target
 * @returns {boolean}
 */
function isEventInsideFindBar(target) {
  return target instanceof Element && Boolean(target.closest('[data-table-find-bar]'));
}

/**
 * @param {EventTarget | null} target
 * @returns {boolean}
 */
function isEventInsideEditableField(target) {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

/**
 * In-table find (Cmd/Ctrl+F) over loaded rows, including virtualized ones.
 * @param {object} props
 * @param {TableGroupsCustom[]} props.tableGroups
 * @param {ApiManagerTableColumns[]} [props.columns]
 * @param {TableInstance} [props.table]
 * @param {{ scrollToIndex: (index: number, options?: { align?: string }) => void, getVirtualItems: () => Array<{ index: number }> }} [props.groupVirtualizer]
 * @param {Record<string, boolean>} props.isGroupAccordionOpen
 * @param {(groupId: string) => void} props.toggleGroup
 * @returns {{
 *   isOpen: boolean,
 *   query: string,
 *   setQuery: (query: string) => void,
 *   matches: TableFindEntry[],
 *   currentMatchIndex: number,
 *   scrollTarget: TableFindScrollTarget | null,
 *   highlightedRowId: string | null,
 *   next: () => void,
 *   prev: () => void,
 *   close: () => void,
 *   open: () => void,
 *   clearScrollTarget: () => void,
 * }}
 */
export default function useTableFind({
  tableGroups,
  columns,
  table,
  groupVirtualizer,
  isGroupAccordionOpen,
  toggleGroup,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQueryState] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [scrollTarget, setScrollTarget] = useState(/** @type {TableFindScrollTarget | null} */ (null));
  const [highlightedRowId, setHighlightedRowId] = useState(/** @type {string | null} */ (null));

  const visibleColumnIdsKey = table
    ? getPinnedAwareVisibleLeafColumns(table).map((column) => column.id).join('\0')
    : '';

  const findIndex = useMemo(() => {
    const visibleIds = visibleColumnIdsKey
      ? new Set(visibleColumnIdsKey.split('\0'))
      : null;
    const findColumns = (columns ?? []).filter((column) => !visibleIds || visibleIds.has(column.id));
    return buildTableFindIndex(tableGroups ?? [], findColumns);
  }, [tableGroups, columns, visibleColumnIdsKey]);

  const matches = useMemo(
    () => findMatchingRows(findIndex, query),
    [findIndex, query],
  );
  const safeMatchIndex = matches.length === 0
    ? 0
    : Math.min(currentMatchIndex, matches.length - 1);

  const clearScrollTarget = useCallback(() => {
    setScrollTarget(null);
  }, []);

  const isGroupAccordionOpenRef = useRef(isGroupAccordionOpen);
  isGroupAccordionOpenRef.current = isGroupAccordionOpen;
  const toggleGroupRef = useRef(toggleGroup);
  toggleGroupRef.current = toggleGroup;
  const groupVirtualizerRef = useRef(groupVirtualizer);
  groupVirtualizerRef.current = groupVirtualizer;

  const scrollToMatch = useCallback(
    /** @param {TableFindEntry | undefined} match */
    (match) => {
      if (!match) {
        setHighlightedRowId(null);
        setScrollTarget(null);
        return;
      }
      const groupIsOpen = isGroupAccordionOpenRef.current[match.groupId] ?? true;
      if (!groupIsOpen) {
        toggleGroupRef.current(match.groupId);
      }
      const renderedGroupIndices = new Set(
        (groupVirtualizerRef.current?.getVirtualItems() ?? []).map((item) => item.index),
      );
      if (!renderedGroupIndices.has(match.groupIndex)) {
        groupVirtualizerRef.current?.scrollToIndex(match.groupIndex, { align: 'start' });
      }
      setHighlightedRowId(match.rowId);
      setScrollTarget({
        groupId: match.groupId,
        rowId: match.rowId,
        rowIndexInGroup: match.rowIndexInGroup,
      });
    },
    [],
  );

  const setQuery = useCallback(
    /** @param {string} nextQuery */
    (nextQuery) => {
      setQueryState(nextQuery);
      setCurrentMatchIndex(0);
      scrollToMatch(findMatchingRows(findIndex, nextQuery)[0]);
    },
    [findIndex, scrollToMatch],
  );

  const goToIndex = useCallback(
    /** @param {number} nextIndex */
    (nextIndex) => {
      setCurrentMatchIndex(nextIndex);
      scrollToMatch(matches[nextIndex]);
    },
    [matches, scrollToMatch],
  );

  const next = useCallback(() => {
    if (!matches.length) return;
    goToIndex(cycleFindMatchIndex(safeMatchIndex, matches.length, 1));
  }, [goToIndex, matches.length, safeMatchIndex]);

  const prev = useCallback(() => {
    if (!matches.length) return;
    goToIndex(cycleFindMatchIndex(safeMatchIndex, matches.length, -1));
  }, [goToIndex, matches.length, safeMatchIndex]);

  const close = useCallback(() => {
    setIsOpen(false);
    setQueryState('');
    setCurrentMatchIndex(0);
    setHighlightedRowId(null);
    setScrollTarget(null);
  }, []);

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  useEffect(() => {
    /**
     * @param {KeyboardEvent} event
     */
    const handleKeyDown = (event) => {
      if (event.isComposing) return;
      if (isEventInsideDialog(event.target)) return;

      const isFindShortcut = (event.metaKey || event.ctrlKey)
        && !event.altKey
        && event.key.toLowerCase() === 'f';

      if (isFindShortcut) {
        event.preventDefault();
        setIsOpen(true);
        requestAnimationFrame(() => {
          const input = document.getElementById('table-find-input');
          if (input instanceof HTMLInputElement) {
            input.focus();
            input.select();
          }
        });
        return;
      }

      if (isOpen && event.key === 'Enter' && isEventInsideFindBar(event.target)) {
        event.preventDefault();
        if (event.shiftKey) {
          prev();
        } else {
          next();
        }
        return;
      }

      if (event.key === 'Escape' && isOpen) {
        const inFindBar = isEventInsideFindBar(event.target);
        const inEditable = isEventInsideEditableField(event.target);
        if (inFindBar || !inEditable) {
          event.preventDefault();
          close();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [close, isOpen, next, prev]);

  return {
    isOpen,
    query,
    setQuery,
    matches,
    currentMatchIndex: safeMatchIndex,
    scrollTarget,
    highlightedRowId,
    next,
    prev,
    close,
    open,
    clearScrollTarget,
  };
}
